import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Gem, Loader2, Lock, ShieldCheck } from "lucide-react";
import { SEO } from "@/components/common/SEO";
import { useAuth } from "@/context/AuthContext";
import { brand } from "@/config/brand";

const inputCls = "w-full border border-ivory/15 bg-ivory/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/30 focus:border-gold focus:outline-none";

export default function AdminLoginPage() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email.trim(), password.trim());
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.user?.role !== "admin") {
      await logout();
      setError("This account does not have admin access.");
      return;
    }
    navigate("/admin", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4 py-12" data-testid="admin-login-page">
      <SEO title="Admin Sign In" />
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 text-ivory">
          <span className="grid h-10 w-10 place-items-center border border-gold/40 text-gold"><Gem size={18} /></span>
          <div>
            <p className="font-serif text-xl tracking-[0.18em]">{brand.name}</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Admin Console</p>
          </div>
        </div>

        <h1 className="mt-12 font-serif text-3xl font-light text-ivory">Sign in</h1>
        <p className="mt-2 text-sm text-ivory/60">Restricted area. Staff credentials only.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" noValidate data-testid="admin-login-form">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ivory/60">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" className={`mt-1.5 ${inputCls}`} data-testid="admin-login-email" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ivory/60">Password</span>
            <span className="relative mt-1.5 block">
              <input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className={`${inputCls} pr-11`} data-testid="admin-login-password" />
              <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"} data-testid="admin-login-toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/50 hover:text-gold">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </span>
          </label>
          {error && (
            <p className="flex items-start gap-2 border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" role="alert" data-testid="admin-login-error">
              <Lock size={13} className="mt-0.5 shrink-0" /> {error}
            </p>
          )}
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="inline-flex w-full items-center justify-center gap-2 bg-gold px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ivory disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {busy ? "Signing in…" : "Sign in to Admin"}
          </button>
        </form>

        <p className="mt-8 text-center text-[11px] text-ivory/35">Access is restricted to ÉLORA JEWELS staff.</p>
      </div>
    </div>
  );
}
